import { ApiError } from '@community_dev/requests'
import { UseMutationOptions, UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query'
import { notification } from 'antd'

import {
  CreateSSOConfigInput,
  SSOConfigResponse,
  UpsertSSOConfigInput,
  deleteSSOConfig,
  postSSOConfig,
  putSSOConfig,
} from 'api/sso'
import { QUERIES } from 'constants/queries'

type UpsertSSOBody = Omit<UpsertSSOConfigInput, 'id'>

export const useCreateSSOConfigMutation = (
  options?: UseMutationOptions<SSOConfigResponse, ApiError, CreateSSOConfigInput>,
): UseMutationResult<SSOConfigResponse, ApiError, CreateSSOConfigInput> => {
  const queryClient = useQueryClient()
  return useMutation<SSOConfigResponse, ApiError, CreateSSOConfigInput>((values) => postSSOConfig(values), {
    onSuccess: (data, variables, context) => {
      const id = data?.data?.id
      if (id) {
        queryClient.setQueryData([QUERIES.SSO_CONFIG, id], data)
        queryClient.invalidateQueries([QUERIES.SSO_CONFIG, id])
      }
      notification.success({ message: 'SSO config created' })
      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useUpdateSSOConfigMutation = (
  id: string,
  options?: UseMutationOptions<SSOConfigResponse, ApiError, UpsertSSOBody>,
): UseMutationResult<SSOConfigResponse, ApiError, UpsertSSOBody> => {
  const queryClient = useQueryClient()
  return useMutation<SSOConfigResponse, ApiError, UpsertSSOBody>((values) => putSSOConfig({ id, ...values }), {
    onSuccess: (data, variables, context) => {
      queryClient.setQueryData([QUERIES.SSO_CONFIG, id], data)
      queryClient.invalidateQueries([QUERIES.SSO_CONFIG, id])
      queryClient.invalidateQueries([QUERIES.SSO_CONFIGS])
      notification.success({ message: 'SSO config saved' })
      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useDeleteSSOConfigMutation = (
  id: string,
  options?: UseMutationOptions<void, ApiError, void>,
): UseMutationResult<void, ApiError, void> => {
  const queryClient = useQueryClient()
  return useMutation<void, ApiError, void>(() => deleteSSOConfig(id), {
    onSuccess: (data, variables, context) => {
      queryClient.removeQueries([QUERIES.SSO_CONFIG, id])
      queryClient.invalidateQueries([QUERIES.SSO_CONFIGS])
      notification.success({ message: 'SSO config deleted' })
      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}
